// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { type GameState, type TreatmentCard } from '../../types/game';
import { Button } from '../Button';
import { TreatmentCardList } from '../TreatmentCard';

interface JudgingSectionProps {
  isJudge: boolean;
  playerId: string;
  playerSubmissions: GameState['player_submissions'];
  allPlayers: GameState['all_players'];
  onSelectWinner: (winnerId: string) => void;
  submitting: boolean;
}

export function JudgingSection({
  isJudge,
  playerId,
  playerSubmissions,
  allPlayers,
  onSelectWinner,
  submitting,
}: JudgingSectionProps) {
  return (
    <div className="glass rounded-lg p-4 sm:p-6 mb-6" data-testid="game-judging-section">
      <h2 className="font-display text-xl sm:text-2xl font-bold mb-3 sm:mb-4">
        {isJudge ? 'Choose the Winner!' : 'Submissions'}
      </h2>
      <div className="space-y-3 sm:space-y-4">
        {Object.entries(playerSubmissions).map(([pid, cards]) => {
          if (pid === playerId && !isJudge) return null;
          if (cards === 'submitted') return null;

          const player = allPlayers.find(p => p.player_id === pid);
          if (!player) return null;

          return (
            <div key={pid} className="bg-white/10 rounded-lg p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3 mb-2 sm:mb-3">
                <p className="font-semibold text-sm sm:text-base">
                  {player.display_name}'s Submission
                </p>
                {isJudge && (
                  <Button
                    variant="success"
                    onClick={() => onSelectWinner(pid)}
                    disabled={submitting}
                    className="w-full sm:w-auto py-1 sm:py-2 min-h-9 sm:min-h-10 text-sm"
                  >
                    Choose Winner
                  </Button>
                )}
              </div>
              <TreatmentCardList cards={cards as TreatmentCard[]} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
